import { object } from "../helpers/object";
import { debug } from "../helpers/debug";

/*------------------------------------*\
  # STICKY

  Gestion d'élement sitcky dans son parent

  [data-js-component="sticky"] 
  [data-js-sticky="{false|top|bottom}"]
  [data-js-sticky-target-space="bool"] ajout un padding a l'élément devenu sticky
  [data-js-sticky-target-width="HTMLElement"] remplace la width par default de l'élément sticky par le width de la cible
  [data-js-sticky-target-left="HTMLElement"] positionne l'élément sticky left par rapport a la cible
  [data-js-sticky-target-scroll-x="HTMLElement"] suit le scroll de la cible
\*------------------------------------*/
class Sticky {

  constructor(module){
    this.MODULE = module;
    this.DOM_MODULE = object.toArray(document.querySelectorAll(this.MODULE));
    this.gap = 12;
  }
  /**
   * INITIALISATION
   * @returns 
   */
  init = () => {
    if (!this.DOM_MODULE.length) return;

    // SCROLL EVENT HANDLER
    document.addEventListener('scroll', this.scrollHandler);
    // RESPONSIVE HANDLER
    this.responsiveHandler();
    
    // RESIZE EVENT HANDLER
    window.addEventListener('resize', this.resizeHandler);
  


    this.scrollXHandler();

    if(debug.DEBUG) console.log('components/sticky.js');
  };

  /**
   * RESIZE HANDLER
   * @param {event} event 
   */
  resizeHandler = (event) => {
    this.DOM_MODULE.forEach(element => {
      this.setStickyWidth(element);
      this.setTargetHeight(element);
      this.setTargetLeft(element);
    })
  }
  /**
   * RESPONSIVE
   */
  responsiveHandler = () => {
    document.addEventListener('RESPONSIVE_HAS_CHANGED', (event) => {
      this.DOM_MODULE.forEach(element => {
        this.setStickyWidth(element);
        this.setTargetHeight(element);
        this.setTargetLeft(element);
      })
    })
  };
  /**
   * SCROLL HANDLER
   * @param {event} event 
   */
  scrollHandler = (event) => {
    this.stickyHandler();
  };
  /**
   * Handles the horizontal scroll event for elements with the attribute 
   * `data-js-sticky-target-scroll-x`. It updates the CSS variable 
   * `--sticky-scroll-x` based on the scroll position of the target element.
   *
   * @function
   * @name scrollXHandler
   * @memberof Sticky
   * @instance
   * @description This function finds all elements with the attribute 
   * `data-js-sticky-target-scroll-x`, attaches a scroll event listener to 
   * the target element specified by the attribute, and updates the 
   * `--sticky-scroll-x` CSS variable to reflect the horizontal scroll position.
   */
  scrollXHandler = () => {
    this.DOM_MODULE
      .filter(element => element.matches('[data-js-sticky-target-scroll-x]'))
      .forEach(element => {
        const targetElement = document.querySelector(element.getAttribute('data-js-sticky-target-scroll-x'));
        if (targetElement) {
          targetElement.addEventListener('scroll', event => {
            element.style.setProperty('--sticky-scroll-x', `${-event.target.scrollLeft}px`);
          });
        }
      });
  }
  /**
   * STICKY HANDLER
   * Handles the sticky behavior for elements within the DOM_MODULE.
   * Iterates over each element, calculates the necessary positions and gaps,
   * and sets the appropriate CSS variables and attributes to achieve the sticky effect.
   *
   * @function stickyHandler
   * @memberof StickyComponent
   * @instance
   *
   * @example
   * // Example usage:
   * stickyHandler();
   *
   * @returns {void}
   */
  stickyHandler = () => {
    this.DOM_MODULE.forEach(element => {
      const target = element.querySelector('.o-sticky__target');
      const gap = target.matches('[data-js-sticky-gap]') ? parseInt(target.getAttribute('data-js-sticky-gap')) : this.gap;
      const heightDifference = target.offsetHeight - window.innerHeight + gap;
      const startTop = heightDifference > 0 && !target.matches('[data-js-sticky-always="true"]') ? -heightDifference : gap;
      const posTop = element.getBoundingClientRect().top - startTop;
      const posBot = posTop + element.offsetHeight - target.offsetHeight;
  
      if (element.offsetHeight === target.offsetHeight) return;
  
      element.style.setProperty('--sticky-start-top', `${startTop}px`);
      this.setTargetHeight(element);
  
      if (posTop <= 0) {
        element.setAttribute('data-js-sticky', posBot > 0 ? 'top' : 'bottom');
      } else {
        element.setAttribute('data-js-sticky', false);
      }
    });
  }
  /**
   * setStickyWidth
   * 
   * definition de la longueur de l'element 
   * sticky. Par default longeur du conteneur.
   * Peut aussi avoir une autre taille cf m-comparator
   * @param {HTMLElement} element 
   */
  setStickyWidth = (element) => {
    element.style.removeProperty('--sticky-width');
    
    // Temporarily disable sticky positioning to get accurate width
    const currentStickyState = element.getAttribute('data-js-sticky');
    const wasSticky = currentStickyState && currentStickyState !== 'false';
    
    if (wasSticky) {
      element.setAttribute('data-js-sticky', 'false');
    }
    
    // Force a reflow to ensure the position change takes effect
    void element.offsetHeight;

    const targetSelector = element.getAttribute('data-js-sticky-target-width');
    const w = targetSelector ? element.querySelector(targetSelector)?.offsetWidth || element.offsetWidth : element.offsetWidth;
    
    // Restore sticky state
    if (wasSticky) {
      element.setAttribute('data-js-sticky', currentStickyState);
    }
    
    element.style.setProperty('--sticky-width', `${w}px`);
  };
  
  /**
   * setTargetHeight
   * 
   * quand sticky on compense la hauteur de l'élément
   * devenu sticky
   * @param {HTMLElement} element 
   */
  setTargetHeight = (element) => {
    element.style.removeProperty('--sticky-target-height');
    const targetElement = element.querySelector('.o-sticky__target');
    if (targetElement) {
      element.style.setProperty('--sticky-target-height', `${targetElement.offsetHeight}px`);
    }
  }
  /**
   * Sets the CSS custom properties '--sticky-target-left' and '--sticky-left' on the given element.
   * 
   * This function calculates the left position of the target element specified by the 
   * 'data-js-sticky-target-left' attribute of the given element and sets it as the value 
   * of the '--sticky-target-left' custom property. It also sets the left position of the 
   * given element as the value of the '--sticky-left' custom property.
   * 
   * @param {HTMLElement} element - The element on which to set the CSS custom properties.
   */
  setTargetLeft = (element) => {
    element.style.removeProperty('--sticky-target-left');
    element.style.removeProperty('--sticky-left');
  
    const target = document.querySelector(element.getAttribute('data-js-sticky-target-left'));
    if (target) {
      const { left: targetLeft } = target.getBoundingClientRect();
      const { left: elementLeft } = element.getBoundingClientRect();
      element.style.setProperty('--sticky-target-left', `${targetLeft}px`);
      element.style.setProperty('--sticky-left', `${elementLeft}px`);
    }
  };
}
export const sticky = new Sticky('[data-js-component="sticky"]'); 